import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AddMemberModal = ({ onClose, onAdd, existingMembers }) => {
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [color, setColor] = useState('#3B82F6');
  const [validationError, setValidationError] = useState('');

  const predefinedColors = [
    '#EF4444', '#F97316', '#F59E0B', '#EAB308',
    '#84CC16', '#10B981', '#14B8A6', '#06B6D4',
    '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7',
    '#EC4899', '#F43F5E'
  ];

  const roleOptions = [
    { value: 'Mom', label: 'Mom' },
    { value: 'Dad', label: 'Dad' },
    { value: 'Son', label: 'Son' },
    { value: 'Daughter', label: 'Daughter' },
    { value: 'Grandparent', label: 'Grandparent' },
    { value: 'Other', label: 'Other' }
  ];

  const generateInitials = (name) => {
    const parts = name?.trim()?.split(' ');
    if (parts?.length >= 2) {
      return (parts?.[0]?.[0] + parts?.[parts?.length - 1]?.[0])?.toUpperCase();
    }
    return name?.substring(0, 2)?.toUpperCase();
  };

  const handleNameChange = (e) => {
    const newName = e?.target?.value;
    setName(newName);
    
    // Check for duplicate names
    const isDuplicate = existingMembers?.some(
      m => m?.name?.toLowerCase() === newName?.toLowerCase()
    );
    
    if (isDuplicate) {
      setValidationError('This name is already in use');
    } else if (newName?.trim()?.length === 0) {
      setValidationError('');
    } else {
      setValidationError('');
    }
  };

  const handleSubmit = () => {
    if (!name?.trim() || !role) {
      setValidationError('Please fill in all required fields');
      return;
    }

    if (validationError) {
      return;
    }

    const newMember = {
      name: name?.trim(),
      role,
      color,
      initials: generateInitials(name)
    };

    onAdd(newMember);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[300] p-4">
      <div className="bg-popover border border-border rounded-lg shadow-warm-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-h3 font-heading text-foreground">
            Add Family Member
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="x" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <Input
            label="Name"
            placeholder="Enter name"
            value={name}
            onChange={handleNameChange}
            error={validationError}
            required
          />

          <Select
            label="Role"
            value={role}
            onChange={setRole}
            options={roleOptions}
            placeholder="Select role"
            required
          />

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Color <span className="text-destructive">*</span>
            </label>
            <div className="grid grid-cols-7 gap-2">
              {predefinedColors?.map((colorOption) => (
                <button
                  key={colorOption}
                  className={`w-10 h-10 rounded-lg transition-all hover:scale-110 active:scale-95 ${
                    color === colorOption ? 'ring-2 ring-ring ring-offset-2' : ''
                  }`}
                  style={{ backgroundColor: colorOption }}
                  onClick={() => setColor(colorOption)}
                  aria-label={`Select color ${colorOption}`}
                />
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="bg-muted rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-2">Preview:</p>
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-semibold"
                style={{ backgroundColor: color }}
              >
                {name ? generateInitials(name) : '??'}
              </div>
              <div>
                <p className="font-semibold text-foreground">
                  {name || 'Name'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {role || 'Role'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            fullWidth
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={!name?.trim() || !role || !!validationError}
            fullWidth
          >
            Add Member
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddMemberModal;